import React, { Suspense } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router";

// Infra (kept static — shell, guards, always needed)
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./components/common/ScrollToTop";
import AttributionCapture from './components/common/AttributionCapture';
import ErrorBoundary from "./components/common/ErrorBoundary";
import PrivateRoute from './components/auth/PrivateRoute';
import { useLanguageSync } from './hooks/useLanguageSync';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import AppDownloadBanner from './components/pwa/AppDownloadBanner';
import { usePWA } from './hooks/usePWA';
import CookieConsent from './components/legal/CookieConsent';
import LegalModal from './components/legal/LegalModal';
import { useLegalModal } from './hooks/useLegalModal';
import { useAppUpdater } from './hooks/useAppUpdater';
import DemoBanner from './components/demo/DemoBanner';
import DemoCapture from './components/demo/DemoCapture';
import DemoVetoToast from './components/demo/DemoVetoToast';
import { instalaContadorDeAcciones } from './utils/demo';

// Envuelve window.fetch para contar las acciones del visitante en la demo. Se
// instala una sola vez, a nivel de módulo, y fuera de la demo no hace nada.
instalaContadorDeAcciones();

// Pages (lazy — code-split per route to shrink the initial bundle)
const SignIn = lazyWithRetry(() => import("./pages/AuthPages/SignIn"));
const SignUp = lazyWithRetry(() => import("./pages/AuthPages/SignUp"));
const Registro = lazyWithRetry(() => import("./pages/AuthPages/Registro"));
const ConfirmEmail = lazyWithRetry(() => import("./pages/AuthPages/ConfirmEmail"));
const ForgotPassword = lazyWithRetry(() => import("./pages/Auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/Auth/ResetPassword"));
const NotFound = lazyWithRetry(() => import("./pages/OtherPage/NotFound"));
const DemoEntry = lazyWithRetry(() => import("./pages/Demo/DemoEntry"));
const UserProfiles = lazyWithRetry(() => import("./pages/UserProfiles"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard/Dashboard"));
const FirstRunFlow = lazyWithRetry(() => import("./pages/Onboarding/FirstRunFlow"));
const ServiciosList = lazyWithRetry(() => import("./pages/Servicios/ServiciosList"));
const HistorialServicios = lazyWithRetry(() => import("./pages/Servicios/HistorialServicios"));
const ServicioDetail = lazyWithRetry(() => import("./pages/Servicios/ServicioDetail"));
const NewServicio = lazyWithRetry(() => import("./pages/Servicios/NewServicio"));
const NewSolicitud = lazyWithRetry(() => import("./pages/Solicitudes/NewSolicitud"));
const EditSolicitud = lazyWithRetry(() => import("./pages/Solicitudes/EditSolicitud"));
const EditServicio = lazyWithRetry(() => import("./pages/Servicios/EditServicio"));
const ConfiguracionGeneral = lazyWithRetry(() => import("./pages/Configuracion/ConfiguracionGeneral"));
const ClientesList = lazyWithRetry(() => import("./pages/Clientes/ClientesList"));
const ClienteDetail = lazyWithRetry(() => import("./pages/Clientes/ClienteDetail"));
const NewCliente = lazyWithRetry(() => import("./pages/Clientes/NewCliente"));
const EditCliente = lazyWithRetry(() => import("./pages/Clientes/EditCliente"));
const TarifasList = lazyWithRetry(() => import("./pages/Tarifas/TarifasList"));
const NewTarifa = lazyWithRetry(() => import("./pages/Tarifas/NewTarifa"));
const EditTarifa = lazyWithRetry(() => import("./pages/Tarifas/EditTarifa"));
const FacturasList = lazyWithRetry(() => import("./pages/Facturacion/FacturasList"));
const ServiciosPendientes = lazyWithRetry(() => import('./pages/Facturacion/ServiciosPendientes'));
const FacturaForm = lazyWithRetry(() => import("./pages/Facturacion/FacturaForm"));
const FacturaEdit = lazyWithRetry(() => import("./pages/Facturacion/FacturaEdit"));
const FacturaView = lazyWithRetry(() => import("./pages/Facturacion/FacturaView"));
const FacturaTemplates = lazyWithRetry(() => import('./pages/Facturacion/FacturaTemplates'));
const FacturaPreview = lazyWithRetry(() => import('./pages/Facturacion/FacturaPreview'));
const CierreTrimestre = lazyWithRetry(() => import('./pages/Facturacion/CierreTrimestre'));
const GastosList = lazyWithRetry(() => import('./pages/Gastos/GastosList'));
const GastoForm = lazyWithRetry(() => import('./pages/Gastos/GastoForm'));
const NotificationsPage = lazyWithRetry(() => import('./pages/Notifications/NotificationsPage'));
const ArchivosList = lazyWithRetry(() => import('./pages/Archivos/ArchivosList'));
const Settings = lazyWithRetry(() => import('./pages/Settings/Settings'));
const VersionsPage = lazyWithRetry(() => import('./pages/Settings/VersionsPage'));
const HelpCenter = lazyWithRetry(() => import('./pages/Support/HelpCenter'));
const AIChat = lazyWithRetry(() => import('./pages/Support/AIChat'));
const NewTicket = lazyWithRetry(() => import('./pages/Support/NewTicket'));
const TicketsList = lazyWithRetry(() => import('./pages/Support/TicketsList'));
const TicketDetail = lazyWithRetry(() => import('./pages/Support/TicketDetail'));
const SubscriptionPage = lazyWithRetry(() => import('./pages/Subscription/SubscriptionPage'));
const SubscriptionSuccess = lazyWithRetry(() => import('./pages/suscripcion/SubscriptionSuccess'));
const NumeracionSettings = lazyWithRetry(() => import('./pages/Settings/NumeracionSettings'));
const AnalyticsPage = lazyWithRetry(() => import('./pages/Analytics/AnalyticsPage'));
const ReferralsPage = lazyWithRetry(() => import('./pages/Referrals/ReferralsPage'));
const LegalNotice = lazyWithRetry(() => import('./pages/Legal/LegalNotice'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/Legal/PrivacyPolicy'));
const Terms = lazyWithRetry(() => import('./pages/Legal/Terms'));
const CookiePolicy = lazyWithRetry(() => import('./pages/Legal/CookiePolicy'));
const ContractConditions = lazyWithRetry(() => import('./pages/Legal/ContractConditions'));
const RefundPolicy = lazyWithRetry(() => import('./pages/Legal/RefundPolicy'));
const AcceptableUsePolicy = lazyWithRetry(() => import('./pages/Legal/AcceptableUsePolicy'));
const ResponsibleDeclaration = lazyWithRetry(() => import('./pages/Legal/ResponsibleDeclaration'));
const DeleteAccount = lazyWithRetry(() => import('./pages/Legal/DeleteAccount'));

// Admin panel (superadmin only)

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
  </div>
);

// Old /partners/solicitudes/:id deep links → resolve to local /servicios/:id
function PartnersSolicitudRedirect() {
  const { id: externalId } = useParams<{ id: string }>();
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const [target, setTarget] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!externalId || !token) { setTarget('/servicios?source=Partners'); return; }
    fetch(`${import.meta.env.VITE_API_URL}/api/servicios?limit=200&agencyFilter=Partners`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((j) => {
        const items = Array.isArray(j) ? j : (j.data || []);
        const match = items.find((s: any) => String(s.external_request_id) === String(externalId));
        setTarget(match ? `/servicios/${match.id}` : '/servicios?source=Partners');
      })
      .catch(() => setTarget('/servicios?source=Partners'));
  }, [externalId, token]);
  if (!target) return <div className="p-6 text-sm text-gray-500">Cargando…</div>;
  return <Navigate to={target} replace />;
}

export default function App() {
  // Initialize language synchronization
  useLanguageSync();

  // Initialize PWA functionality
  usePWA();

  // Auto-update: detect new deploys and force reload
  useAppUpdater();

  // Legal modal state
  const { isOpen, modalType, closeModal } = useLegalModal();

  return (
    <>
      {/* Cookie Consent Banner */}
      <CookieConsent />

      {/* Legal Modal */}
      {modalType && (
        <LegalModal
          isOpen={isOpen}
          onClose={closeModal}
          type={modalType}
        />
      )}

      <Router>
        <ScrollToTop />
        <AttributionCapture />

        {/* PWA Install Prompt - Inside Router context */}
        <PWAInstallPrompt />

        {/* Invitación a la app nativa (App Store en iOS, Google Play en Android; snooze 30 días) */}
        <AppDownloadBanner />

        {/* Demostración pública: se pintan solos y solo si la sesión es la demo */}
        <DemoBanner />
        <DemoCapture />
        <DemoVetoToast />

        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            {/* Onboarding primera ejecución (full-screen, sin AppLayout) */}
            <Route path="/bienvenida" element={<FirstRunFlow />} />
            <Route element={<AppLayout />}>
              <Route index path="/" element={<Dashboard />} />

              {/* Core Pages */}
              <Route path="/profile" element={<UserProfiles />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/servicios" element={<ServiciosList />} />
              {/* Partners pages now live inside /servicios — keep deep links working */}
              <Route path="/partners/solicitudes" element={<Navigate to="/servicios?source=Partners" replace />} />
              <Route path="/partners/solicitudes/:id" element={<PartnersSolicitudRedirect />} />
              <Route path="/servicios/historial" element={<HistorialServicios />} />
              <Route path="/servicios/:id" element={<ServicioDetail />} />
              <Route path="/servicios/nuevo" element={<NewServicio />} />
              <Route path="/servicios/editar/:id" element={<EditServicio />} />
              {/* Legacy routes - redirect to new paths */}
              <Route path="/solicitudes/nueva" element={<NewSolicitud />} />
              <Route path="/solicitudes/editar/:id" element={<EditSolicitud />} />
              <Route path="/clientes" element={<ClientesList />} />
              <Route path="/clientes/:id" element={<ClienteDetail />} />
              <Route path="/clientes/nuevo" element={<NewCliente />} />
              <Route path="/clientes/editar/:id" element={<EditCliente />} />
              <Route path="/facturacion/tarifas" element={<TarifasList />} />
              <Route path="/facturacion/tarifas/nueva" element={<NewTarifa />} />
              <Route path="/facturacion/tarifas/editar/:id" element={<EditTarifa />} />
              <Route path="/facturacion" element={<FacturasList />} />
              <Route path="/facturacion/historial" element={<FacturasList />} />
              <Route path="/facturacion/pendientes" element={<ServiciosPendientes />} />
              <Route path="/facturacion/new" element={<FacturaForm />} />
              <Route path="/facturacion/editar/:id" element={<FacturaEdit />} />
              <Route path="/facturacion/preview/:id" element={<FacturaPreview />} />
              <Route path="/facturacion/templates" element={<FacturaTemplates />} />
              <Route path="/facturacion/cierre-trimestre" element={<CierreTrimestre />} />
              {/* Gastos Pages - NUEVAS RUTAS (independientes) */}
              <Route path="/gastos" element={<GastosList />} />
              <Route path="/gastos/nuevo" element={<GastoForm />} />
              <Route path="/gastos/editar/:id" element={<GastoForm />} />
              {/* Configuration Pages */}
              <Route path="/servicios/configuracion" element={<ConfiguracionGeneral />} />
              <Route path="/notifications" element={<NotificationsPage />} /> {/* Add route for NotificationsPage */}
              <Route path="/archivos" element={<ArchivosList />} /> {/* Add route for ArchivosList */}
              <Route path="/referidos" element={<ReferralsPage />} /> {/* Add route for ReferralsPage */}
              <Route path="/suscripcion" element={<SubscriptionPage />} /> {/* Add route for SubscriptionPage */}
              <Route path="/suscripcion/success" element={<SubscriptionSuccess />} /> {/* Add route for SubscriptionSuccess */}
              <Route path="/settings" element={<Settings />} /> {/* Add route for Settings */}
              <Route path="/settings/numeracion" element={<NumeracionSettings />} /> {/* Add route for NumeracionSettings */}
              <Route path="/versions" element={<VersionsPage />} /> {/* Add route for VersionsPage */}
              <Route path="/analytics" element={<AnalyticsPage />} /> {/* Add route for Analytics */}

              {/* Support System Routes */}
              <Route path="/support/help-center" element={<HelpCenter />} />
              <Route path="/support/chat" element={<AIChat />} />
              <Route path="/support/tickets" element={<TicketsList />} />
              <Route path="/support/tickets/new" element={<NewTicket />} />
              <Route path="/support/tickets/:id" element={<TicketDetail />} />

              {/* Admin Panel Routes (superadmin only) */}
              {/* Panel de administración movido a admin.guidemeapp.es (apps/admin) */}

            </Route>
          </Route>

          {/* Auth Layout */}
          {/* Demostración pública: entra sola, sin registro. Ver pages/Demo/DemoEntry */}
          <Route path="/demo" element={<DemoEntry />} />

          <Route path="/signin" element={<SignIn />} />
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Legal Pages - Public Access */}
          <Route path="/legal/notice" element={<LegalNotice />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/eliminar-cuenta" element={<DeleteAccount />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
          <Route path="/legal/contract" element={<ContractConditions />} />
          <Route path="/legal/refund" element={<RefundPolicy />} />
          <Route path="/legal/acceptable-use" element={<AcceptableUsePolicy />} />
          <Route path="/legal/responsible-declaration" element={<ResponsibleDeclaration />} />

          {/* Fallback Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </Router>
    </>
  );
}
